import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router'
import { ChevronDown, ChevronUp, Building, Radio } from 'lucide-react'
import { GamesButton, useGamesSummary } from '@/features/games'
import { paths } from '@/routes/paths'
import { useActivity } from './activity'
import { CallTiles } from './CallTiles'
import { useChatLines } from './chatLog'
import { MediaButtons } from './MediaButtons'
import { Minimap } from './Minimap'
import { useOfficeLive, usePosRef } from './OfficeLive'

const KEY = 'tw.officewidget.collapsed'
const readCollapsed = () => {
  try {
    return window.localStorage.getItem(KEY) === '1'
  } catch {
    return false
  }
}

/**
 * The office, on every page: a small live map, what has just happened, and your microphone and camera.
 * It is how people find out someone is in the kitchen, or that a colleague has started on something,
 * without going to look. It steps aside on the office page itself, which is all of this at full size.
 */
export function OfficeWidget({ workspaceSlug }: { workspaceSlug: string }) {
  const live = useOfficeLive()
  const posRef = usePosRef()
  const { pathname } = useLocation()
  const activity = useActivity()
  const chat = useChatLines()
  const games = useGamesSummary(live.data?.me ?? '')
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const box = useRef<HTMLElement>(null)
  const shown = Boolean(live.data && live.world) && !pathname.endsWith('/office')
  /*
   * Tell the page how much room this takes. The app's main column pads its bottom by this, so the
   * last rows of a table or board can always be scrolled up above the widget instead of hiding under it.
   */
  useEffect(() => {
    const root = document.documentElement
    const el = box.current
    if (!shown || !el) {
      root.style.removeProperty('--tt-float-h')
      return
    }
    const apply = () => root.style.setProperty('--tt-float-h', `${Math.ceil(el.getBoundingClientRect().height) + 24}px`)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => {
      ro.disconnect()
      root.style.removeProperty('--tt-float-h')
    }
  }, [shown, collapsed])
  const data = live.data
  const world = live.world

  const feed = useMemo(
    () =>
      [...activity.map((a) => ({ id: a.id, at: a.at, text: a.text })), ...chat.map((c) => ({ id: c.id, at: c.at, text: `${c.userId === data?.me ? 'You' : c.name.split(' ')[0]}: “${c.text}”` }))]
        .sort((a, b) => b.at - a.at)
        .slice(0, 4),
    [activity, chat, data?.me],
  )

  if (pathname.endsWith('/office') || !data || !world) return null
  const people = data.people
  const roaming = people.filter((p) => p.pos && p.userId !== data.me)
  const me = people.find((p) => p.userId === data.me)
  const working = people.filter((p) => p.working).length
  const talking = live.calls.map((r) => people.find((p) => p.userId === r.userId)?.displayName.split(' ')[0] ?? 'Someone')

  const toggle = () => {
    const next = !collapsed
    setCollapsed(next)
    try {
      window.localStorage.setItem(KEY, next ? '1' : '0')
    } catch {
      /* the choice just lasts until the page reloads */
    }
  }

  return (
    <aside ref={box} className="fixed bottom-4 right-3 z-30 w-72 max-w-[calc(100vw-1.5rem)] rounded-xl border border-border bg-card shadow-lg" aria-label="The office">
      <div className="flex items-center gap-2 px-3 py-2">
        <Building className="size-4 text-brand" />
        <Link to={paths.office(workspaceSlug)} className="min-w-0 flex-1 truncate text-sm font-semibold hover:underline">
          The office
          <span className="font-normal text-text-muted"> · {roaming.length + 1} here{working ? ` · ${working} working` : ''}</span>
        </Link>
        <button type="button" onClick={toggle} className="grid size-7 place-items-center rounded-md hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring" aria-label={collapsed ? 'Expand the office' : 'Collapse the office'} aria-expanded={!collapsed}>
          {collapsed ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
        </button>
      </div>
      {collapsed ? null : (
        <div className="grid gap-2 border-t border-border-soft p-3">
          <Link to={paths.office(workspaceSlug)} className="block" aria-label="Open the office">
            <Minimap world={world} roaming={roaming} posRef={posRef} className="w-full" />
          </Link>
          {feed.length ? (
            <ul className="grid gap-1 text-xs text-text-secondary" aria-label="What is happening">
              {feed.map((f) => (
                <li key={f.id} className="truncate" title={f.text}>
                  {f.text}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-text-muted">Quiet for now. Things that happen in the office show up here.</p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <MediaButtons />
            {talking.length ? (
              <span className="flex items-center gap-1 text-xs font-medium text-success">
                <Radio className="size-3.5" /> With {talking.join(', ')}
              </span>
            ) : live.mic || live.cam ? (
              <span className="text-xs text-text-muted">Walk up to someone</span>
            ) : null}
          </div>
          {live.writes ? (
            <div className="flex items-center justify-between gap-2 border-t border-border-soft pt-2">
              <GamesButton me={data.me} />
              <span className="min-w-0 truncate text-xs text-text-muted">
                {games.waiting ? 'Your move is waiting' : games.open ? `${games.open} open table${games.open === 1 ? '' : 's'}` : games.playing ? `${games.playing} game${games.playing === 1 ? '' : 's'} in play` : 'Chess-free zone. Try Connect Four.'}
              </span>
            </div>
          ) : null}
          <CallTiles calls={live.calls} people={people} local={live.cam ? live.localStream : null} meName={me?.displayName ?? 'You'} />
        </div>
      )}
    </aside>
  )
}
